import { Port } from '@ais-ui/api-spec/portserver';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { v4 as uuidv4 } from 'uuid';
import { createGeoJsonFromGeometry, Geometry } from '@ais-ui/utils';

export function createShapeDictionary(
  data: Record<string, string | string[] | Geometry | Geometry[]>,
  shapes: GeoJson[],
  adHoc = false
): Record<string, GeoJson> {
  const newShapeDictionary: Record<string, GeoJson> = {};

  for (const key in data) {
    if (Array.isArray(data[key])) {
      let features: GeoJson[] = [];
      if (adHoc) {
        // Handle multiple geometries
        features = (data[key] as Geometry[]).map((geometry: Geometry) => {
          return createGeoJsonFromGeometry(geometry);
        });
      } else {
        // Handle multiple shape IDs
        features = shapes.filter((shape) =>
          (data[key] as string[]).includes(shape.properties.id as string)
        );
      }

      newShapeDictionary[key] = createFeatureCollection(features);
    } else {
      let matchingShape: GeoJson | undefined;
      // Handle single shape ID
      if (adHoc) {
        matchingShape = createGeoJsonFromGeometry(data[key] as Geometry);
      } else {
        matchingShape = shapes.find(
          (shape) => shape.properties.id === data[key]
        );
      }

      if (matchingShape) {
        newShapeDictionary[key] = matchingShape;
      }
    }
  }

  return newShapeDictionary;
}

export function createPortDictionary(
  data: Record<string, string | string[]>,
  ports: Port[]
): Record<string, string[]> {
  const newPortDictionary: Record<string, string[]> = {};

  for (const key in data) {
    const value = data[key];
    // Convert value to array if it's a single string
    const portIds = Array.isArray(value) ? value : [value];

    // Filter valid ports and map to string IDs
    const matchingPorts = ports
      .filter((port) => {
        const portStringId = port.id ? port.id.toString() : '';
        return portIds.includes(portStringId);
      })
      .map((port) => port.id!.toString());

    if (matchingPorts.length > 0) {
      newPortDictionary[key] = matchingPorts;
    }
  }

  return newPortDictionary;
}

export function createWorldDictionary(
  data: Record<string, boolean>
): Record<string, boolean> {
  const newWorldDictionary: Record<string, boolean> = {};

  for (const key in data) {
    newWorldDictionary[key] = true;
  }

  return newWorldDictionary;
}

function createFeatureCollection(features: GeoJson[]): GeoJson {
  return {
    features: features,
    properties: {
      id: uuidv4(),
      name: 'temporary feature collection',
      owner: 'reports',
    },
    type: 'FeatureCollection',
  };
}
